import React, { useRef, useCallback, useMemo, useState } from 'react';
import {
    PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    LineChart, Line,
} from 'recharts';
import { sankey as d3Sankey, sankeyLinkHorizontal } from 'd3-sankey';
import { Button, Card, makeStyles, Text } from '@fluentui/react-components';
import { ArrowDownloadRegular } from '@fluentui/react-icons';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F'];

const useStyles = makeStyles({
    chartContainer: {
        width: '100%',
        boxSizing: 'border-box',
        padding: '16px',
        margin: '12px 0',
        backgroundColor: '#1E1E1E',
        borderRadius: '8px',
    },
    chartHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
    },
    chartTitle: {
        color: '#E0E0E0',
        fontSize: '14px',
        fontWeight: '600',
    },
    exportButton: {
        minWidth: 'auto',
        padding: '4px 8px',
        fontSize: '11px',
        color: '#999',
    },
    customTooltip: {
        backgroundColor: '#333',
        padding: '8px 12px',
        borderRadius: '4px',
        border: '1px solid #555',
    },
    tooltipLabel: {
        color: '#AAA',
        margin: '0 0 4px 0',
        fontSize: '11px',
    },
    tooltipRow: {
        margin: '2px 0',
        fontSize: '12px',
    },
    sankeyContainer: {
        width: '100%',
        position: 'relative',
    },
    sankeySvg: {
        display: 'block',
    },
    sankeyLinkPath: {
        cursor: 'pointer',
        transition: 'stroke-opacity 0.15s',
    },
    sankeyTooltip: {
        position: 'absolute',
        transform: 'translateX(-50%)',
        backgroundColor: '#333',
        padding: '6px 12px',
        borderRadius: '4px',
        border: '1px solid #555',
        color: '#E0E0E0',
        fontSize: '12px',
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        zIndex: 10,
    },
    dataTable: {
        width: '100%',
        borderCollapse: 'collapse',
        color: '#E0E0E0',
    },
    tableViewport: {
        maxWidth: '100%',
        maxHeight: '560px',
        overflow: 'auto',
        border: '1px solid #333',
        borderRadius: '6px',
    },
    tableToolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '10px',
    },
    tableFilter: {
        width: 'min(320px, 100%)',
        boxSizing: 'border-box',
        padding: '6px 10px',
        color: '#E0E0E0',
        backgroundColor: '#252525',
        border: '1px solid #555',
        borderRadius: '4px',
        font: 'inherit',
        ':focus': {
            outline: '1px solid #888',
            outlineOffset: '-1px',
        },
    },
    tableRowCount: {
        color: '#999',
        fontSize: '12px',
        whiteSpace: 'nowrap',
    },
    tableHeadCell: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        minWidth: '120px',
        textAlign: 'left',
        padding: '8px',
        borderBottom: '1px solid #555',
        backgroundColor: '#1E1E1E',
        whiteSpace: 'nowrap',
    },
    tableHeadCellLeft: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        minWidth: '120px',
        textAlign: 'left',
        padding: '8px',
        borderBottom: '1px solid #555',
        backgroundColor: '#1E1E1E',
    },
    tableHeadCellRight: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        minWidth: '96px',
        textAlign: 'right',
        padding: '8px',
        borderBottom: '1px solid #555',
        backgroundColor: '#1E1E1E',
        whiteSpace: 'nowrap',
    },
    tableHeaderButton: {
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        gap: '6px',
        padding: 0,
        color: 'inherit',
        backgroundColor: 'transparent',
        border: 0,
        font: 'inherit',
        fontWeight: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
    },
    tableHeaderButtonRight: {
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '6px',
        padding: 0,
        color: 'inherit',
        backgroundColor: 'transparent',
        border: 0,
        font: 'inherit',
        fontWeight: 'inherit',
        textAlign: 'right',
        cursor: 'pointer',
    },
    tableRow: {
        ':nth-child(even)': {
            backgroundColor: '#242424',
        },
        ':hover': {
            backgroundColor: '#2C2C2C',
        },
    },
    tableCell: {
        padding: '6px 8px',
        borderBottom: '1px solid #333',
        overflowWrap: 'anywhere',
    },
    tableCellRight: {
        textAlign: 'right',
        padding: '6px 8px',
        borderBottom: '1px solid #333',
        whiteSpace: 'nowrap',
    },
    tableEmptyCell: {
        padding: '24px 8px',
        color: '#999',
        textAlign: 'center',
    },
});

interface ChartDataPoint {
    label: string;
    value: number;
}

interface ChartSeries {
    name: string;
    data: ChartDataPoint[];
}

interface SankeyNode {
    name: string;
}

interface SankeyLink {
    source: number;
    target: number;
    value: number;
}

interface SankeyData {
    nodes: SankeyNode[];
    links: SankeyLink[];
}

export type TableCellValue = string | number | boolean | null;

export interface TableColumn {
    key: string;
    label: string;
    type?: 'text' | 'number';
}

type TableRow = Record<string, TableCellValue>;
type TableSortDirection = 'ascending' | 'descending';

interface TableSort {
    key: string;
    direction: TableSortDirection;
}

export interface ChartSpec {
    type: 'pie' | 'bar' | 'line' | 'table' | 'sankey';
    title?: string;
    data?: ChartDataPoint[];
    series?: ChartSeries[];
    sankeyData?: SankeyData;
    xLabel?: string;
    yLabel?: string;
    stacked?: boolean;
    columns?: TableColumn[];
    rows?: Array<Record<string, TableCellValue>>;
}

interface AnalyticsChartProps {
    spec: ChartSpec;
}

const formatNumber = (value: number): string => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toLocaleString();
};

const formatTableValue = (value: unknown): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') return Number.isFinite(value) ? value.toLocaleString() : String(value);
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'string') return value;
    return String(value);
};

const compareTableValues = (left: TableCellValue | undefined, right: TableCellValue | undefined, type?: TableColumn['type']): number => {
    const leftMissing = left === null || left === undefined;
    const rightMissing = right === null || right === undefined;
    if (leftMissing && rightMissing) return 0;
    if (leftMissing) return 1;
    if (rightMissing) return -1;

    if (type === 'number' && typeof left === 'number' && typeof right === 'number') {
        return left - right;
    }

    return formatTableValue(left).localeCompare(formatTableValue(right), undefined, {
        numeric: true,
        sensitivity: 'base',
    });
};

const escapeCsvValue = (value: TableCellValue | undefined): string => {
    let text = value === null || value === undefined ? '' : String(value);
    if (typeof value === 'string' && /^[\t\r\n ]*[=+\-@]/.test(text)) {
        text = `'${text}`;
    }
    return `"${text.replace(/"/g, '""')}"`;
};

const MultiSeriesCustomTooltip = ({ active, payload, label }: any) => {
    const classes = useStyles();
    if (active && payload && payload.length) {
        return (
            <div className={classes.customTooltip}>
                <p className={classes.tooltipLabel}>{label}</p>
                {payload.map((entry: any, index: number) => (
                    <p key={index} className={classes.tooltipRow} style={{ color: entry.color }}>
                        {entry.name}: <strong>{entry.value?.toLocaleString()}</strong>
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

const D3SankeyChart: React.FC<{ sankeyData: SankeyData; title?: string }> = ({ sankeyData }) => {
    const classes = useStyles();
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = React.useState(600);
    const [hoverLink, setHoverLink] = React.useState<number | null>(null);
    const [tooltip, setTooltip] = React.useState<{ x: number; y: number; text: string } | null>(null);
    const nodeWidth = 14;
    const nodePadding = 28;
    const margin = { left: 8, right: 30, top: 30, bottom: 10 };

    React.useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                setContainerWidth(entry.contentRect.width);
            }
        });
        observer.observe(containerRef.current);
        setContainerWidth(containerRef.current.clientWidth);
        return () => observer.disconnect();
    }, []);

    const layout = useMemo(() => {
        const nodes = sankeyData.nodes.map(n => ({ ...n }));
        const links = sankeyData.links.map(l => ({ ...l }));
        const height = Math.max(300, nodes.length * 55);

        const generator = (d3Sankey() as any)
            .nodeId((_d: any, i: number) => i)
            .nodeWidth(nodeWidth)
            .nodePadding(nodePadding)
            .extent([[margin.left, margin.top], [containerWidth - margin.right, height - margin.bottom]])
            .nodeSort(null);

        const result = generator({ nodes: nodes as any, links: links as any });
        return { ...result, height };
    }, [sankeyData, containerWidth]);

    const linkPath = sankeyLinkHorizontal();
    const maxDepth = Math.max(...layout.nodes.map((n: any) => n.depth || 0));

    return (
        <div
            ref={containerRef}
            className={classes.sankeyContainer}
            onMouseMove={(e) => {
                if (hoverLink === null) return;
                const rect = containerRef.current?.getBoundingClientRect();
                setTooltip(prev => prev ? {
                    ...prev,
                    x: e.clientX - (rect?.left || 0),
                    y: e.clientY - (rect?.top || 0) - 40,
                } : null);
            }}
        >
            <svg width={containerWidth} height={layout.height} className={classes.sankeySvg}>
                {/* Links */}
                {layout.links.map((link: any, i: number) => (
                    <path
                        key={`link-${i}`}
                        d={linkPath(link) || ''}
                        fill="none"
                        stroke={COLORS[link.source.index % COLORS.length]}
                        strokeOpacity={hoverLink === i ? 0.6 : 0.3}
                        strokeWidth={Math.max(1, link.width)}
                        className={classes.sankeyLinkPath}
                        onMouseEnter={(e) => {
                            setHoverLink(i);
                            const rect = containerRef.current?.getBoundingClientRect();
                            setTooltip({
                                x: e.clientX - (rect?.left || 0),
                                y: e.clientY - (rect?.top || 0) - 40,
                                text: `${link.source.name} → ${link.target.name}: ${link.value?.toLocaleString()}`
                            });
                        }}
                        onMouseLeave={() => { setHoverLink(null); setTooltip(null); }}
                    />
                ))}
                {/* Nodes */}
                {layout.nodes.map((node: any, i: number) => {
                    const nodeHeight = (node.y1 || 0) - (node.y0 || 0);
                    const x0 = node.x0 || 0;
                    const x1 = node.x1 || 0;
                    const y0 = node.y0 || 0;
                    const depth = node.depth || 0;
                    // Left column: label to the right of node; right column: label to the left; middle: above
                    const isFirst = depth === 0;
                    const isLast = depth === maxDepth;
                    let labelX: number, anchor: 'start' | 'end' | 'middle', labelY: number;
                    if (isFirst) {
                        labelX = x1 + 6;
                        anchor = 'start';
                        labelY = y0 + nodeHeight / 2;
                    } else if (isLast) {
                        labelX = x0 - 6;
                        anchor = 'end';
                        labelY = y0 + nodeHeight / 2;
                    } else {
                        labelX = x0 + (x1 - x0) / 2;
                        anchor = 'middle';
                        labelY = y0 - 14;
                    }
                    return (
                        <g key={`node-${i}`}>
                            <rect
                                x={x0}
                                y={y0}
                                width={x1 - x0}
                                height={nodeHeight}
                                fill={COLORS[i % COLORS.length]}
                                opacity={0.9}
                                rx={2}
                            />
                            <text
                                x={labelX}
                                y={isFirst || isLast ? labelY - 7 : labelY}
                                textAnchor={anchor}
                                dominantBaseline="middle"
                                fill="#E0E0E0"
                                fontSize={11}
                                fontFamily="system-ui, -apple-system, sans-serif"
                                fontWeight={600}
                            >
                                {node.name}
                            </text>
                            <text
                                x={labelX}
                                y={isFirst || isLast ? labelY + 7 : labelY + 13}
                                textAnchor={anchor}
                                dominantBaseline="middle"
                                fill="#999"
                                fontSize={9}
                                fontFamily="system-ui, -apple-system, sans-serif"
                            >
                                {(node.value || 0).toLocaleString()}
                            </text>
                        </g>
                    );
                })}
            </svg>
            <div
                className={classes.sankeyTooltip}
                style={{
                    left: tooltip?.x ?? 0,
                    top: tooltip?.y ?? 0,
                    visibility: tooltip ? 'visible' : 'hidden',
                }}
            >
                {tooltip?.text ?? ''}
            </div>
        </div>
    );
};

const AnalyticsChart: React.FC<AnalyticsChartProps> = ({ spec }) => {
    const classes = useStyles();
    const chartRef = useRef<HTMLDivElement>(null);
    const [tableFilter, setTableFilter] = useState('');
    const [tableSort, setTableSort] = useState<TableSort | null>(null);
    const { type, title, data, series, sankeyData, xLabel, yLabel, stacked, columns, rows } = spec;

    const handleExport = useCallback(() => {
        if (!chartRef.current) return;
        const svg = chartRef.current.querySelector('svg');
        if (!svg) return;

        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        canvas.width = svg.clientWidth * 2;
        canvas.height = svg.clientHeight * 2;

        img.onload = () => {
            if (!ctx) return;
            ctx.fillStyle = '#1E1E1E';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            const link = document.createElement('a');
            link.download = `${(title || 'chart').replace(/[^a-zA-Z0-9]/g, '_')}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        };
        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    }, [title]);

    const hasMultiSeries = series && series.length > 0;
    const hasSingleData = data && data.length > 0;

    const hasSankeyData = sankeyData && sankeyData.nodes && sankeyData.links;
    const hasDynamicTable = type === 'table'
        && Array.isArray(columns)
        && columns.length > 0
        && columns.every(column =>
            column
            && typeof column.key === 'string'
            && column.key.length > 0
            && typeof column.label === 'string'
            && column.label.length > 0
            && (column.type === undefined || column.type === 'text' || column.type === 'number'))
        && new Set(columns.map(column => column.key)).size === columns.length
        && Array.isArray(rows)
        && rows.every(row => row && typeof row === 'object' && !Array.isArray(row));

    const tableColumns = useMemo<TableColumn[]>(() => {
        if (hasDynamicTable) return columns!;
        if (type === 'table' && hasSingleData) {
            return [
                { key: 'label', label: xLabel || 'Item', type: 'text' },
                { key: 'value', label: yLabel || 'Value', type: 'number' },
            ];
        }
        return [];
    }, [columns, hasDynamicTable, hasSingleData, type, xLabel, yLabel]);

    const tableSourceRows = useMemo<TableRow[]>(() => {
        if (hasDynamicTable) return rows!;
        if (type === 'table' && hasSingleData) {
            return data!.map(item => ({ label: item.label, value: item.value }));
        }
        return [];
    }, [data, hasDynamicTable, hasSingleData, rows, type]);

    const visibleTableRows = useMemo(() => {
        const normalizedFilter = tableFilter.trim().toLocaleLowerCase();
        const filteredRows = tableSourceRows
            .map((row, originalIndex) => ({ row, originalIndex }))
            .filter(({ row }) =>
                normalizedFilter.length === 0
                || tableColumns.some(column =>
                    formatTableValue(row[column.key]).toLocaleLowerCase().includes(normalizedFilter)));

        const sortColumn = tableSort
            ? tableColumns.find(column => column.key === tableSort.key)
            : undefined;
        if (!sortColumn || !tableSort) return filteredRows;

        return [...filteredRows].sort((left, right) => {
            const leftValue = left.row[sortColumn.key];
            const rightValue = right.row[sortColumn.key];
            const leftMissing = leftValue === null || leftValue === undefined;
            const rightMissing = rightValue === null || rightValue === undefined;
            if (leftMissing && rightMissing) return 0;
            if (leftMissing) return 1;
            if (rightMissing) return -1;

            const result = compareTableValues(
                leftValue,
                rightValue,
                sortColumn.type);
            return tableSort.direction === 'ascending' ? result : -result;
        });
    }, [tableColumns, tableFilter, tableSort, tableSourceRows]);

    const handleTableSort = useCallback((key: string) => {
        setTableSort(current => ({
            key,
            direction: current?.key === key && current.direction === 'ascending'
                ? 'descending'
                : 'ascending',
        }));
    }, []);

    const handleTableExport = useCallback(() => {
        if (tableColumns.length === 0) return;

        const csv = [
            tableColumns.map(column => escapeCsvValue(column.label)).join(','),
            ...visibleTableRows.map(({ row }) =>
                tableColumns.map(column => escapeCsvValue(row[column.key])).join(',')),
        ].join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${(title || 'table').replace(/[^a-zA-Z0-9]/g, '_')}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    }, [tableColumns, title, visibleTableRows]);

    if (!hasMultiSeries && !hasSingleData && !hasSankeyData && !hasDynamicTable) return null;

    // Single-series data for pie/bar/table and single-line charts
    const chartData = hasSingleData ? data!.map(d => ({ name: d.label, value: d.value })) : [];

    // Multi-series: merge all series into one array with shared x-axis labels
    let multiSeriesData: any[] = [];
    let seriesNames: string[] = [];
    if (hasMultiSeries) {
        seriesNames = series!.map(s => s.name);
        // Collect all unique labels in order
        const labelSet = new Map<string, any>();
        for (const s of series!) {
            for (const d of s.data) {
                if (!labelSet.has(d.label)) {
                    labelSet.set(d.label, { name: d.label });
                }
                labelSet.get(d.label)[s.name] = d.value;
            }
        }
        multiSeriesData = Array.from(labelSet.values());
    }

    return (
        <Card className={classes.chartContainer}>
            <div className={classes.chartHeader}>
                {title ? <Text className={classes.chartTitle}>{title}</Text> : <span />}
                <Button
                    className={classes.exportButton}
                    appearance="subtle"
                    icon={<ArrowDownloadRegular />}
                    onClick={type === 'table' ? handleTableExport : handleExport}
                >
                    {type === 'table' ? 'Export CSV' : 'Export'}
                </Button>
            </div>
            <div ref={chartRef}>

            {type === 'pie' && hasSingleData && (
                <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                        <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            labelLine={true}
                            label={({ name, percent }: any) => `${name} (${(percent * 100).toFixed(1)}%)`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                            isAnimationActive={false}
                        >
                            {chartData.map((_, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip content={<MultiSeriesCustomTooltip />} isAnimationActive={false} wrapperStyle={{ transition: "opacity 0.15s ease" }} />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            )}

            {type === 'bar' && hasSingleData && !hasMultiSeries && (
                <ResponsiveContainer width="100%" height={Math.max(300, data!.length * 35)}>
                    <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                        <XAxis type="number" tickFormatter={formatNumber} stroke="#999" />
                        <YAxis type="category" dataKey="name" width={120} stroke="#999" tick={{ fontSize: 11 }} />
                        <Tooltip content={<MultiSeriesCustomTooltip />} isAnimationActive={false} wrapperStyle={{ transition: "opacity 0.15s ease" }} />
                        <Bar dataKey="value" fill="#0088FE" radius={[0, 4, 4, 0]} isAnimationActive={false} />
                    </BarChart>
                </ResponsiveContainer>
            )}

            {type === 'bar' && hasMultiSeries && (
                <ResponsiveContainer width="100%" height={Math.max(350, multiSeriesData.length * 45)}>
                    <BarChart data={multiSeriesData} margin={{ left: 20, right: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                        <XAxis dataKey="name" stroke="#999" tick={{ fontSize: 10 }} />
                        <YAxis tickFormatter={formatNumber} stroke="#999" />
                        <Tooltip content={<MultiSeriesCustomTooltip />} isAnimationActive={false} wrapperStyle={{ transition: "opacity 0.15s ease" }} />
                        <Legend />
                        {seriesNames.map((name, index) => (
                            <Bar
                                key={name}
                                dataKey={name}
                                fill={COLORS[index % COLORS.length]}
                                radius={stacked ? [0, 0, 0, 0] : [4, 4, 0, 0]}
                                stackId={stacked ? 'stack' : undefined}
                                isAnimationActive={false}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            )}

            {type === 'line' && hasMultiSeries && (
                <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={multiSeriesData} margin={{ left: 20, right: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                        <XAxis dataKey="name" stroke="#999" tick={{ fontSize: 10 }} />
                        <YAxis tickFormatter={formatNumber} stroke="#999" />
                        <Tooltip content={<MultiSeriesCustomTooltip />} isAnimationActive={false} wrapperStyle={{ transition: "opacity 0.15s ease" }} />
                        <Legend />
                        {seriesNames.map((name, index) => (
                            <Line
                                key={name}
                                type="monotone"
                                dataKey={name}
                                stroke={COLORS[index % COLORS.length]}
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                isAnimationActive={false}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            )}

            {type === 'line' && hasSingleData && !hasMultiSeries && (
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData} margin={{ left: 20, right: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                        <XAxis dataKey="name" stroke="#999" tick={{ fontSize: 10 }} />
                        <YAxis tickFormatter={formatNumber} stroke="#999" />
                        <Tooltip content={<MultiSeriesCustomTooltip />} isAnimationActive={false} wrapperStyle={{ transition: "opacity 0.15s ease" }} />
                        <Line type="monotone" dataKey="value" stroke="#0088FE" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
                    </LineChart>
                </ResponsiveContainer>
            )}

            {type === 'sankey' && hasSankeyData && (
                <D3SankeyChart sankeyData={sankeyData!} title={title} />
            )}

            {type === 'table' && tableColumns.length > 0 && (
                <>
                    <div className={classes.tableToolbar}>
                        <input
                            type="search"
                            className={classes.tableFilter}
                            aria-label="Filter table rows"
                            placeholder="Filter rows..."
                            value={tableFilter}
                            onChange={event => setTableFilter(event.target.value)}
                        />
                        <span className={classes.tableRowCount} aria-live="polite">
                            {visibleTableRows.length === tableSourceRows.length
                                ? `${tableSourceRows.length} ${tableSourceRows.length === 1 ? 'row' : 'rows'}`
                                : `${visibleTableRows.length} of ${tableSourceRows.length} rows`}
                        </span>
                    </div>
                    <div className={classes.tableViewport}>
                        <table className={classes.dataTable}>
                            <thead>
                                <tr>
                                    {tableColumns.map(column => {
                                        const activeSort = tableSort?.key === column.key
                                            ? tableSort.direction
                                            : 'none';
                                        return (
                                            <th
                                                key={column.key}
                                                scope="col"
                                                aria-sort={activeSort}
                                                className={column.type === 'number'
                                                    ? classes.tableHeadCellRight
                                                    : classes.tableHeadCell}
                                            >
                                                <button
                                                    type="button"
                                                    className={column.type === 'number'
                                                        ? classes.tableHeaderButtonRight
                                                        : classes.tableHeaderButton}
                                                    aria-label={`Sort by ${column.label}`}
                                                    onClick={() => handleTableSort(column.key)}
                                                >
                                                    <span>{column.label}</span>
                                                    {activeSort !== 'none' && (
                                                        <span aria-hidden="true">
                                                            {activeSort === 'ascending' ? '^' : 'v'}
                                                        </span>
                                                    )}
                                                </button>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {visibleTableRows.map(({ row, originalIndex }) => (
                                    <tr key={originalIndex} className={classes.tableRow}>
                                        {tableColumns.map(column => (
                                            <td
                                                key={column.key}
                                                className={column.type === 'number'
                                                    ? classes.tableCellRight
                                                    : classes.tableCell}
                                            >
                                                {formatTableValue(row[column.key])}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                                {visibleTableRows.length === 0 && (
                                    <tr>
                                        <td className={classes.tableEmptyCell} colSpan={tableColumns.length}>
                                            No matching rows
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
            </div>
        </Card>
    );
};

export default React.memo(AnalyticsChart);
